import { PictureOutlined } from '@ant-design/icons';
import { Image } from 'antd';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import styles from './PreviewImage.module.scss';

export interface PreviewImageProps {
  src?: string | null;
  alt?: string;
  size?: number;
  shape?: 'square' | 'circle';
  fallback?: ReactNode;
  className?: string;
  style?: CSSProperties;
  fill?: boolean;
  stopPropagation?: boolean;
}

function joinClasses(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(' ');
}

export function PreviewImage({
  src,
  alt = 'Image preview',
  size = 32,
  shape = 'square',
  fallback,
  className,
  style,
  fill = false,
  stopPropagation = false,
}: PreviewImageProps) {
  const resolvedSrc = src?.trim();
  const shapeClass = shape === 'circle' ? styles.circle : styles.square;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  const dimensionStyle: CSSProperties = fill
    ? { width: '100%', height: '100%', ...style }
    : { width: size, height: size, ...style };

  if (!resolvedSrc) {
    return (
      <span
        className={joinClasses(styles.placeholder, shapeClass, className)}
        style={dimensionStyle}
      >
        {fallback ?? <PictureOutlined />}
      </span>
    );
  }

  return (
    <span
      className={joinClasses(styles.wrap, shapeClass, fill && styles.fill, className)}
      style={dimensionStyle}
      onClick={stopPropagation ? handleClick : undefined}
    >
      <Image
        src={resolvedSrc}
        alt={alt}
        width={fill ? '100%' : size}
        height={fill ? '100%' : size}
        preview={{ src: resolvedSrc }}
      />
    </span>
  );
}
